<script setup lang="ts">
    import { TransitionRoot, TransitionChild, Dialog, DialogPanel, DialogTitle } from '@headlessui/vue';

    const { t } = useI18n({
        useScope: 'local',
    });

    const isLg = ref(false);
    useMediaQuery([
        {
            query: defaultQueries.min.lg,
            ref: isLg,
        },
    ]);

    const dialogKey = ref(0);
    const isOpen = ref(false);

    const toggleModal = () => {
        isOpen.value = !isOpen.value;
    };

    const resetDialog = () => {
        dialogKey.value++;
    };

    useOnNavigation(resetDialog);
</script>
<template>
    <div class="mol-algolia-filters-dialog mb-8">
        <template v-if="!isLg">
            <atm-button
                styling="outline"
                @click="toggleModal">
                {{ t('filters') }}
                <template #icon>
                    <woom-icon-filter class="size-6" />
                </template>
            </atm-button>
            <!-- Basically a duplicate of mol-dialog but with small css tweaks. -->
            <!-- :unmount="false" is needed for filters not to be dismounted from the dom -->
            <transition-root
                appear
                :key="dialogKey"
                :show="isOpen"
                as="template"
                :unmount="false">
                <Dialog
                    as="div"
                    :open="isOpen"
                    @close="toggleModal"
                    class="relative z-xl"
                    :unmount="false">
                    <transition-child
                        as="template"
                        enter="duration-700 ease-out"
                        enter-from="opacity-0"
                        enter-to="opacity-100"
                        leave="duration-300 ease-out"
                        leave-from="opacity-100"
                        leave-to="opacity-0"
                        :unmount="false">
                        <atm-overlay />
                    </transition-child>

                    <div class="fixed inset-0 overflow-hidden">
                        <div class="flex min-h-full items-end justify-center md:items-center">
                            <transition-child
                                as="template"
                                enter="duration-700 ease-out"
                                enter-from="opacity-0 translate-y-9"
                                enter-to="opacity-100 translate-y-0"
                                leave="duration-300 ease-out "
                                leave-from="opacity-100 translate-y-0"
                                leave-to="opacity-0 translate-y-9"
                                :unmount="false">
                                <dialog-panel
                                    class="flex max-h-dvh min-h-44 w-full max-w-3xl transform flex-col gap-4 rounded-t-md bg-woom-white py-6 transition-all md:h-full md:rounded-2xl">
                                    <div class="flex justify-between px-6">
                                        <dialog-title>
                                            <atm-heading size="md">
                                                {{ t('filters') }}
                                            </atm-heading>
                                        </dialog-title>
                                        <atm-button
                                            :aria-label="$t('cta.closeDialog')"
                                            class="translate-x-1/4 -translate-y-1/4"
                                            styling="text"
                                            @click="toggleModal">
                                            <template #icon><woom-icon-close /></template>
                                        </atm-button>
                                    </div>

                                    <!-- small padding to combat overflow scrollbar & refinement "chips" items display not cut off -->
                                    <div class="h-full grow overflow-x-hidden overflow-y-auto py-2">
                                        <slot></slot>
                                    </div>

                                    <div class="px-6 md:ml-auto">
                                        <atm-button
                                            styling="solid-secondary"
                                            @click="toggleModal">
                                            {{ t('applyFilters') }}
                                        </atm-button>
                                    </div>
                                </dialog-panel>
                            </transition-child>
                        </div>
                    </div>
                </Dialog>
            </transition-root>
        </template>

        <template v-else>
            <slot></slot>
        </template>
    </div>
</template>

<i18n lang="json">
{
    "da": {
        "filters": "Filter",
        "applyFilters": "Anvend filtre"
    },
    "de": {
        "filters": "Filter",
        "applyFilters": "Filter anwenden"
    },
    "en": {
        "filters": "Filters",
        "applyFilters": "Apply Filters"
    },
    "fi": {
        "filters": "Suodattimet",
        "applyFilters": "Tallenna rajaukset"
    },
    "fr": {
        "filters": "Filtre",
        "applyFilters": "Appliquer les filtres"
    },
    "nl": {
        "filters": "Filter",
        "applyFilters": "Filters toepassen"
    },
    "sv": {
        "filters": "Filter",
        "applyFilters": "Använd filter"
    },
    "pl": {
        "filters": "Filtry",
        "applyFilters": "Zastosuj filtry"
    },
    "es": {
        "filters": "Filtros",
        "applyFilters": "Aplicar filtros"
    },
    "ro": {
        "filters": "",
        "applyFilters": ""
    }
}
</i18n>
